import { FC, ReactNode } from 'react';

import { Button, ChatEmptyOutline } from '@bonusly/design-system';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { promptGradientBorderSx } from '../bizy/prompt-gradient';
import { dispatchBizyOpenEvent } from './hooks/use-bizy-open-listener';

export type BizyPromptButtonProps = {
	/** Event source for analytics and backend context (e.g. 'admin_insight', 'awards_education'). */
	source: string;
	/** Passed to Bizy as page context (e.g. 'participation_report', 'awards_index'). */
	pageContext: string;
	/** Auto-sent starter message when the panel opens. */
	starterMessage: string;
	/** Button label shown next to the chat icon. @default 'Ask Bizy' */
	label?: string;
	/** Fully custom button content. When provided, overrides the default icon + label. */
	children?: ReactNode;
};

/**
 * Button that opens the Bizy side panel with a pre-seeded starter message.
 *
 * Renders nothing when Bizy is disabled for the company. When placing inside
 * a `<Wrap>` (e.g. `SectionActionFooter`), wrap the call site in a `<WrapItem>`
 * yourself, the same way you would with any other Button.
 *
 * @example
 * <BizyPromptButton
 *   source="admin_insight"
 *   pageContext="participation_report"
 *   starterMessage="Tell me about my participation data."
 * />
 *
 * @example
 * <BizyPromptButton
 *   source="awards_education"
 *   pageContext="awards_index"
 *   starterMessage="Tell me about awards."
 * >
 *   Ask Bizy about awards
 * </BizyPromptButton>
 */
export const BizyPromptButton: FC<BizyPromptButtonProps> = ({ source, pageContext, starterMessage, label = 'Ask Bizy', children }) => {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();

	if (!isBizyEnabledForUser) {
		return null;
	}

	return (
		<Button
			variant="outline"
			colorScheme="secondary"
			size="sm"
			leftIcon={children ? undefined : <ChatEmptyOutline size="sm" />}
			sx={promptGradientBorderSx}
			_hover={{ color: 'text.default' }}
			onClick={() =>
				dispatchBizyOpenEvent({
					source,
					pagePath: window.location.pathname,
					pageContext,
					starterMessage,
				})
			}
		>
			{children ?? label}
		</Button>
	);
};
